/**
 * English catalog. The source of truth for what keys exist.
 *
 * `es.ts` is annotated against this object, so adding a key here and forgetting the
 * Spanish breaks `npm run verify` rather than shipping an English string to an
 * operator who cannot read it.
 *
 * Operator-facing copy is the one place the word "truck" is allowed. Everywhere else
 * in the codebase a forklift is an Asset.
 */
export const en = {
  // ---- chrome ----
  app_name: 'TES Inspect',
  language_english: 'English',
  language_spanish: 'Espanol',
  back: 'Back',
  cancel: 'Cancel',
  continue: 'Continue',
  submit: 'Submit',
  retry: 'Try again',

  // ---- connection state. Always a word, never a bare coloured dot. ----
  status_online: 'Online',
  status_offline: 'Offline - will send when you have signal',
  status_sent: 'Sent',
  status_saved_on_phone: 'Saved on this phone',
  status_send_failed: 'Could not send - tell your supervisor',
  queued_one: '{count} inspection waiting to send',
  queued_other: '{count} inspections waiting to send',

  // ---- truck landing ----
  truck_heading: 'Truck {assetNumber}',
  truck_last_inspected: 'Last inspected {time} by {operator}',
  truck_never_inspected: 'Not inspected yet',
  truck_already_inspected_today: 'Inspected today at {time} by {operator}',
  truck_out_of_service: 'Reported out of service at {time}',
  action_daily_checklist: 'Daily Checklist',
  action_report_problem: 'Report a Problem',
  action_inspect_again: 'Inspect again',

  // ---- identifying the operator ----
  operator_pick_name: 'Who are you?',
  operator_search: 'Search for your name',
  pin_title: 'Enter your PIN',
  pin_wrong: 'That PIN did not match. Try again.',
  pin_locked: 'Too many tries. Wait {minutes} minutes or ask your supervisor.',
  not_you: 'Not you? Switch operator',

  // ---- hour meter ----
  hour_meter_title: 'Hour meter reading',
  hour_meter_hint: 'Last reading: {hours} h',
  hour_meter_skip: 'Cannot read it',

  // ---- checklist ----
  checklist_title: 'Daily check',
  checklist_ok: 'OK',
  checklist_defect: 'Defect',
  checklist_na: 'N/A',
  checklist_finish: 'All others OK - Finish',
  checklist_scroll_first: 'Scroll through the whole list first',
  checklist_attention_items: 'Check these two carefully',

  // ---- defects ----
  defect_title: 'What is wrong?',
  defect_describe: 'Describe the problem',
  defect_photo_required: 'A photo is required',
  defect_take_photo: 'Take photo',
  defect_save: 'Save defect',
  problem_safe_to_operate: 'Is it safe to keep operating?',
  problem_category: 'What kind of problem?',

  // ---- removal from service ----
  // Quotes the regulation and the customer's own policy. It never says TES says stop:
  // the customer, not the dealer, decides whether a truck operates.
  osha_tag_out: 'TAG OUT - DO NOT OPERATE',
  osha_notice_heading: 'This truck must be taken out of service',
  osha_citation: '29 CFR 1910.178(p)(1)',
  osha_acknowledge: 'I have told my supervisor',
  osha_call_supervisor: 'Call supervisor',

  // ---- receipts ----
  receipt_sent_ok: 'Sent - truck OK to operate.',
  receipt_saved_offline: 'Saved on this phone. It will send when you have signal.',
  receipt_defect_sent: 'Reported. TES Service and your manager have been notified.',
  receipt_defect_offline: 'Saved. Your manager will be notified when you have signal.',

  // ---- things that went wrong ----
  tag_unknown_title: 'This tag is not set up yet',
  tag_unknown_body: 'Read this code to your supervisor: {code}',
  tag_unknown_report: 'Report it to TES',
  tag_retired_title: 'This tag was replaced',
  tag_retired_body: 'Look for the new tag on the overhead guard.',
  wrong_company_title: 'This truck belongs to a different company',
  wrong_company_body: 'Ask your supervisor.',
  no_signal_title: 'Cannot check right now',
  no_signal_body: 'No signal. Connect to Wi-Fi or step outside, then scan again.',
} as const;
